import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { toast } from 'sonner';
import { format, parseISO, isBefore, startOfDay, isAfter, differenceInDays } from 'date-fns';
import { Upload, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Edit, Loader2, RotateCw } from 'lucide-react';
import ImageWithLoader from '@/components/ImageWithLoader';
import { Skeleton } from '@/components/ui/skeleton';
import { useCache } from '@/context/CacheContext';
import { compressImage } from '@/lib/imageCompression';
import { rotateImage } from '@/lib/imageUtils';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path}`;
};

export default function CalendarTab({ auth }) {
  const {
    fetchCalendarEntries,
    fetchCurrentChallenge,
    fetchChallengeForMonth,
    fetchProfileStats,
    recordEntryChange
  } = useCache();

  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [displayedChallenge, setDisplayedChallenge] = useState(null);
  const [selectedDateChallenge, setSelectedDateChallenge] = useState(null);
  const [challengeExpanded, setChallengeExpanded] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    completed: false,
    image: null,
    imageDate: null,
    imagePreview: null,
    comment: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [userStats, setUserStats] = useState(null);

  const now = new Date();
  const isBeforeChallenge = now < new Date(2026, 0, 1); // Before Jan 1, 2026
  const isAfterChallenge = now > new Date(2026, 11, 31); // After Dec 31, 2026

  useEffect(() => {
    loadEntries();
    loadCurrentChallenge();
    if (isAfterChallenge) {
      loadUserStats();
    }
    // Set month to Jan 2026 if before challenge or current month if during/after
    if (isBeforeChallenge) {
      setMonth(new Date(2026, 0, 1)); // Jan 2026
    }
  }, []);

  useEffect(() => {
    loadChallengeForMonth(month);
  }, [month]);

  const [initialLoading, setInitialLoading] = useState(true);

  const loadEntries = async () => {
    try {
      const data = await fetchCalendarEntries();
      setEntries(data);
    } catch (error) {
      console.error('Failed to load entries', error);
    } finally {
        setInitialLoading(false);
    }
  };

  const loadCurrentChallenge = async () => {
    try {
      const data = await fetchCurrentChallenge();
      setCurrentChallenge(data);
      setDisplayedChallenge(data);
    } catch (error) {
      console.error('Failed to load challenge', error);
    }
  };

  const loadUserStats = async () => {
    try {
      const data = await fetchProfileStats(auth.user.id);
      setUserStats(data);
    } catch (error) {
      console.error('Failed to load user stats', error);
    }
  };

  const loadChallengeForMonth = async (date) => {
    try {
      const year = date.getFullYear();
      const monthNum = date.getMonth() + 1;

      // Allow loading 2026 challenges even if we're before 2026
      if (year === 2026) {
        const data = await fetchChallengeForMonth(year, monthNum);
        setDisplayedChallenge(data);
      } else {
        setDisplayedChallenge(null);
      }
    } catch (error) {
      console.error('Failed to load challenge for month', error);
      setDisplayedChallenge(null);
    }
  };

  const handleDateClick = async (date) => {
    if (!date) return;

    // Don't allow clicking if before 2026
    if (isBeforeChallenge) {
      toast.info('Challenges start on January 1st, 2026!');
      return;
    }

    const dateStr = format(date, 'yyyy-MM-dd');
    const today = startOfDay(new Date());
    const selectedDay = startOfDay(date);

    // Only allow selection of today or past dates during 2026
    if (!isAfterChallenge && isAfter(selectedDay, today)) {
      toast.error("You can't log entries for future dates");
      return;
    }

    // Check 10-day window restriction only during 2026
    if (!isAfterChallenge) {
      const daysAgo = differenceInDays(today, selectedDay);
      if (daysAgo > 10) {
        toast.error('You can only add or modify entries from the last 10 days');
        return;
      }
    }

    const selectedYear = date.getFullYear();
    const selectedMonth = date.getMonth() + 1;

    if (selectedYear !== 2026) {
      toast.error('Challenge entries are only available for 2026');
      return;
    }

    const existingEntry = entries.find(e => e.date === dateStr);

    if (existingEntry) {
      if (isAfterChallenge) {
        toast.info('Challenge year has ended. Viewing entry only.');
        // If viewing only, we might still want to fetch details if we want to show the image?
        // But the previous behavior was just return. Let's keep it simple for now or fetch if needed.
        // Given the requirement is optimization, let's enable viewing details even after challenge.
        // return;
      }

      // Fetch full details including image
      setFetchingDetails(true);
      try {
        const response = await axios.get(`${API}/entries/${existingEntry.id}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        const fullEntry = response.data;

        setEditingEntry(fullEntry);
        setFormData({
          completed: fullEntry.completed,
          image: fullEntry.image,
          imageDate: null, // Reset date for existing images unless changed
          imagePreview: fullEntry.image,
          comment: fullEntry.comment || ''
        });

        // Load challenge info
        const challengeData = await fetchChallengeForMonth(selectedYear, selectedMonth);
        setSelectedDateChallenge(challengeData);

        setSelectedDate(date);
        setShowEntryForm(true);
      } catch (error) {
        console.error('Failed to load entry details', error);
        toast.error('Failed to load entry details');
      } finally {
        setFetchingDetails(false);
      }
      return;
    } else {
      // Create new entry (not allowed after 2026)
      if (isAfterChallenge) {
        return;
      }
      setEditingEntry(null);
      setFormData({ completed: false, image: null, imageDate: null, imagePreview: null, comment: '' });
    }

    try {
      const data = await fetchChallengeForMonth(selectedYear, selectedMonth);
      setSelectedDateChallenge(data);
    } catch (error) {
      console.error('Failed to load challenge for selected date', error);
      toast.error('No challenge available for this date');
      return;
    }

    setSelectedDate(date);
    setShowEntryForm(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const { base64, dateTaken } = await compressImage(file);
        setFormData({
          ...formData,
          image: base64,
          imageDate: dateTaken,
          imagePreview: base64
        });
      } catch (err) {
        toast.error("Failed to process image");
      }
    }
  };

  const handleRotate = async () => {
    if (!formData.imagePreview) return;

    try {
      // Get the current source (could be base64 or URL)
      // We need to resolve it to a standard URL for crossOrigin handling if it's a relative path
      let imageSource = formData.imagePreview;
      if (imageSource && !imageSource.startsWith('data:') && !imageSource.startsWith('http')) {
        imageSource = getImageUrl(imageSource);
      }

      const rotatedBase64 = await rotateImage(imageSource);
      setFormData({
        ...formData,
        image: rotatedBase64,
        imagePreview: rotatedBase64
      });
    } catch (err) {
      console.error("Failed to rotate image:", err);
      toast.error("Failed to rotate image");
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate) return;

    if (formData.completed && !formData.image) {
      toast.error('Please upload a proof image');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        completed: formData.completed,
        image: formData.image,
        image_date: formData.imageDate,
        comment: formData.comment
      };

      if (editingEntry) {
        // Update existing entry
        await axios.put(
          `${API}/entries/${editingEntry.id}`,
          payload,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        toast.success('Entry updated successfully!');
      } else {
        // Create new entry
        await axios.post(
          `${API}/entries`,
          payload,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        toast.success('Entry saved successfully!');
      }

      setShowEntryForm(false);
      setEditingEntry(null);
      recordEntryChange(); // Update cache for other components

      // Force refresh for immediate update
      const data = await fetchCalendarEntries(true);
      setEntries(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const getDateStatus = (date) => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);

    if (isAfter(checkDate, today)) {
      return null;
    }

    // If we're still loading initial data, everything past/today is pending
    if (initialLoading) {
        return 'pending';
    }

    const dateStr = format(date, 'yyyy-MM-dd');
    const entry = entries.find(e => e.date === dateStr);

    const daysAgo = differenceInDays(today, checkDate);
    if (daysAgo > 10 && !entry) {
      return 'incomplete';
    }

    if (entry) {
      return entry.completed ? 'completed' : 'incomplete';
    }

    return 'pending';
  };

  const modifiers = {
    completed: [],
    incomplete: [],
    pending: []
  };

  const today = new Date();
  for (let i = 1; i <= 31; i++) {
    const date = new Date(month.getFullYear(), month.getMonth(), i);
    if (date.getMonth() === month.getMonth()) {
      const status = getDateStatus(date);
      if (status && modifiers[status]) {
        modifiers[status].push(date);
      }
    }
  }

  const modifiersClassNames = {
    completed: 'calendar-day-completed',
    incomplete: 'calendar-day-incomplete',
    pending: 'calendar-day-pending'
  };

  return (
    <div className="p-4 space-y-6" data-testid="calendar-tab">
      {/* Header */}
      <Card className="p-6 glass" data-testid="calendar-header">
        {initialLoading ? (
            <div className="space-y-6">
                <div className="text-center mb-4">
                  <Skeleton className="h-12 w-3/4 mx-auto mb-2 bg-gray-200" />
                  <Skeleton className="h-4 w-1/3 mx-auto bg-gray-200" />
                </div>
                <div className="bg-white/50 p-6 rounded-xl border-2 border-white/20">
                     <Skeleton className="h-6 w-1/4 mb-4 bg-gray-200" />
                     <Skeleton className="h-4 w-full mb-2 bg-gray-200" />
                     <Skeleton className="h-4 w-full mb-2 bg-gray-200" />
                     <Skeleton className="h-4 w-2/3 mb-4 bg-gray-200" />
                     <div className="pt-4 border-t border-gray-200/50">
                        <Skeleton className="h-5 w-1/3 mb-2 bg-gray-200" />
                        <Skeleton className="h-4 w-1/2 bg-gray-200" />
                     </div>
                </div>
            </div>
        ) : isBeforeChallenge ? (
          <>
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">Get Ready for the 12 Challenges</h2>
            <div className="text-center mb-6">
              <p className="text-xl text-gray-600 mb-2">Challenges begin January 1st, 2026</p>
              <p className="text-gray-500">Explore the upcoming challenges below</p>
            </div>

            {displayedChallenge && (
              <div className="mt-4">
                <div className="text-center mb-4">
                  <h3 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-1">
                    {displayedChallenge.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {displayedChallenge.month && format(new Date(displayedChallenge.year || 2026, (displayedChallenge.month || 1) - 1), 'MMMM yyyy')}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl border-2 border-teal-200">
                  <h4 className="font-semibold text-teal-800 mb-2 text-lg">The Challenge</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">{displayedChallenge.description}</p>

                  <div className="pt-4 border-t border-teal-200">
                    <h4 className="font-semibold text-teal-800 mb-2">Proof of Completion</h4>
                    <p className="text-gray-700 italic">{displayedChallenge.proof_prompt}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : isAfterChallenge ? (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Your 2026 Performance</h2>
            {userStats && (
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl border-2 border-teal-200">
                <h3 className="text-2xl font-bold text-teal-700 mb-4">Year Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-teal-600">{userStats.completion_percentage}%</p>
                    <p className="text-gray-600">Completion Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-cyan-600">{userStats.completed_days}</p>
                    <p className="text-gray-600">Days Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-amber-600">{userStats.medals_count}</p>
                    <p className="text-gray-600">Medals Earned</p>
                  </div>
                </div>
                <p className="text-gray-600 text-center">Scroll through the calendar below to review your daily progress</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* <h2 className="text-3xl font-bold text-gray-800 mb-4">Your Challenge Calendar</h2> */}
            {displayedChallenge && (
              <Collapsible open={challengeExpanded} onOpenChange={setChallengeExpanded}>
                <CollapsibleTrigger className="w-full" data-testid="challenge-toggle">
                  <div className="text-center mb-2 cursor-pointer hover:opacity-80 transition-opacity">
                    <h3 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-1">
                      {displayedChallenge.name}
                    </h3>
                    <p className="text-base text-gray-500 mb-2">
                      {displayedChallenge.month && format(new Date(displayedChallenge.year || 2026, (displayedChallenge.month || 1) - 1), 'MMMM yyyy')}
                    </p>
                    <div className="flex items-center justify-center gap-2 text-teal-600">
                      <span className="text-sm font-medium">
                        {challengeExpanded ? 'Hide Details' : 'View Challenge Details'}
                      </span>
                      {challengeExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl border-2 border-teal-200 mt-4" data-testid="current-challenge-info">
                    <h4 className="font-semibold text-teal-800 mb-2 text-lg">The Challenge</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">{displayedChallenge.description}</p>

                    <div className="pt-4 border-t border-teal-200">
                      <h4 className="font-semibold text-teal-800 mb-2">Proof of Completion</h4>
                      <p className="text-gray-700 italic">{displayedChallenge.proof_prompt}</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </>
        )}
      </Card>

      {!initialLoading && (
        <Card className="p-4 glass" data-testid="calendar-legend">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle size={16} className="text-red-500" />
              <span>Not Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-amber-500" />
              <span>Pending (Last 10 days)</span>
            </div>
          </div>
        </Card>
      )}

      {initialLoading ? (
         <Card className="p-4 glass relative min-h-[400px]">
           <Skeleton className="w-full h-full min-h-[400px] bg-gray-200" />
         </Card>
      ) : (
        <Card className="p-4 glass relative" data-testid="calendar-widget">
          {fetchingDetails && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-md backdrop-blur-sm">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          )}
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateClick}
            month={month}
            onMonthChange={setMonth}
            fromDate={new Date(2026, 0, 1)}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="rounded-md"
          />
        </Card>
      )}

      <Dialog open={showEntryForm} onOpenChange={setShowEntryForm}>
        <DialogContent data-testid="entry-form-dialog" aria-describedby="entry-form-description" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="entry-form-title" className="text-2xl flex items-center gap-2">
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
              {editingEntry && <Edit size={20} className="text-teal-600" />}
            </DialogTitle>
            <p id="entry-form-description" className="sr-only">
              Form to log your daily challenge entry
            </p>
          </DialogHeader>

          {selectedDateChallenge && (
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-200 mb-4">
              <h4 className="text-2xl font-bold text-teal-700 mb-2">{selectedDateChallenge.name}</h4>
            </div>
          )}

          <div className="space-y-4 mt-4">
            <div>
              <Label>Did you complete the challenge?</Label>
              <div className="flex gap-4 mt-2">
                <Button
                  variant={formData.completed ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, completed: true })}
                  className={formData.completed ? 'bg-green-500 hover:bg-green-600' : ''}
                  data-testid="entry-completed-yes"
                >
                  Yes
                </Button>
                <Button
                  variant={!formData.completed ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, completed: false, image: null, imageDate: null, imagePreview: null })}
                  className={!formData.completed ? 'bg-red-500 hover:bg-red-600' : ''}
                  data-testid="entry-completed-no"
                >
                  No
                </Button>
              </div>
            </div>

            {formData.completed && (
              <>
                {selectedDateChallenge && selectedDateChallenge.proof_prompt && (
                  <div className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                    <p className="text-xs font-semibold text-teal-800 mb-1">Proof Required:</p>
                    <p className="text-sm text-gray-700 italic">{selectedDateChallenge.proof_prompt}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="image">Upload Proof Image</Label>
                  <div className="mt-2">
                    <input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="entry-image-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('image').click()}
                      className="w-full"
                      data-testid="entry-upload-button"
                    >
                      <Upload size={16} className="mr-2" />
                      {formData.imagePreview ? 'Change Image' : 'Select Image'}
                    </Button>

                    {formData.imagePreview && (
                      <Button
                        variant="outline"
                        onClick={handleRotate}
                        className="w-full mt-2"
                        data-testid="entry-rotate-button"
                      >
                        <RotateCw size={16} className="mr-2" />
                        Rotate
                      </Button>
                    )}
                  </div>
                  {formData.imagePreview && (
                    <ImageWithLoader
                      src={getImageUrl(formData.imagePreview)}
                      alt="Preview"
                      className="mt-2 image-preview"
                      data-testid="entry-image-preview"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="comment">Add a comment (optional)</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    placeholder="Share your thoughts..."
                    className="mt-1"
                    data-testid="entry-comment-input"
                  />
                </div>
              </>
            )}

            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
              data-testid="entry-submit-button"
            >
              {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Save Entry')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}